import { createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityFeedRow } from '@/activities/ActivityFeedRow';
import api from '@/api';
import { AlertBox } from '@/common/AlertBox';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { LoadMoreWrapper, TimelineWrapper } from '@/common/timelineStyles';
import { WBSButton } from '@/common/WBSButton';
import { GroupHeaderWrapper, Title } from '~/styles/layoutElements';

export const Route = createFileRoute('/_app/_users/members/$memberId/activity')(
  {
    component: MemberActivityRoute,
  },
);

/**
 * Legacy's page size for a member timeline's Load More, sent explicitly because
 * the shared pagination default is 10.
 */
const PAGE_SIZE = 20;

/**
 * One member's activity feed.
 *
 * The group feed's pager without the slug lookup, since the path parameter is
 * already an id. No scope nav, type filter or composer, as legacy's member
 * timeline has none.
 */
function MemberActivityRoute(): ReactElement {
  const { t } = useTranslation();
  const { memberId } = Route.useParams();

  const {
    data,
    isError: feedFailed,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = api.listMemberActivity.useInfiniteQuery(
    { path: { member_id: Number(memberId) }, query: { limit: PAGE_SIZE } },
    {
      initialPageParam: { query: { offset: 0 } },
      // `PaginatedResponse` has no `hasMore`, so it's derived from `count`. The
      // offset is the number of rows already held rather than page index times
      // size; the endpoint's `id` tiebreak keeps the window stable when
      // timestamps collide.
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.reduce((n, page) => n + page.data.length, 0);
        return loaded < lastPage.count
          ? { query: { offset: loaded } }
          : undefined;
      },
    },
  );

  const items = data?.pages.flatMap((page) => page.data) ?? [];
  // The feed query is the gate: `list_member_activity` refuses a student viewer
  // with 403 and an unknown or refused member with 404. Query errors aren't
  // caught above this route, so without this a refused caller sits on a
  // spinner.
  const refused = feedFailed;
  // A pending read shows the heading and a spinner, never the empty state: "No
  // activity found" before anything is read would be wrong, and for a refusal
  // it would reveal whether the member has activity.
  const pending = !refused && data === undefined;

  return (
    <div>
      <GroupHeaderWrapper>
        <Title>{t('wbs.timeline')}</Title>
      </GroupHeaderWrapper>
      {refused ? (
        // Legacy's answer for a refused viewer.
        <AlertBox variant="error">{t('wbs.page-not-found')}</AlertBox>
      ) : pending ? (
        <LoadingSpinner centered />
      ) : items.length === 0 ? (
        <AlertBox variant="info">{t('wbs.sorry-there-was-no')}</AlertBox>
      ) : (
        <TimelineWrapper>
          {items.map((item) => (
            <ActivityFeedRow key={item.id} item={item} />
          ))}
          {hasNextPage && (
            <LoadMoreWrapper>
              <WBSButton
                wide
                alternate
                isLoading={isFetchingNextPage}
                onClick={() => {
                  void fetchNextPage();
                }}
              >
                {t('wbs.load-more')}
              </WBSButton>
            </LoadMoreWrapper>
          )}
        </TimelineWrapper>
      )}
    </div>
  );
}
